import Button from "@/components/atoms/Button";
import { useForm, UseFormReturn } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/atoms/Form";
import { Switch } from "@/components/atoms/Switch";
import Input from "@/components/atoms/Input";
import * as Constants from "@/constants";
import UserSchema from "@/schemas/user";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { CheckboxButton } from "@/components/atoms/Checkbox";
import User from "@/types/User";
import Select from "@/components/molecules/Select";
import { Account } from "@/types/Account";
import Role from "@/types/Role";
import Link from "@/components/atoms/Link";
import ErrorType from "@/types/ErrorType";
import { capitalize } from "@/lib/utils";
import Permission from "@/types/Permission";
import { X } from "lucide-react";

export type UserProps = {
  all_accounts: Account[];
  all_roles: Role[];
  all_countries: any[];
  all_time_zones: string[];
  all_permissions: Permission[];
};

type Props = {
  mode?: "create" | "edit";
  user: User | null;
  edit: boolean;
  onSubmit: (data: User) => void;
  data: UserProps;
  loading: boolean;
  form: UseFormReturn<any>;
  errors?: ErrorType;
};

export const useUserForm = (user: User | null) =>
  useForm({
    resolver: zodResolver(UserSchema),
    defaultValues: user
      ? {
          ...user,
          role_ids: user.roles.map((role) => role.id.toString()) || [],
          account_ids:
            user.accounts.map((account) => account.id.toString()) || [],
          country_ids:
            user.countries.map((country) => country.id.toString()) || [],
        }
      : {
          first_name: "",
          last_name: "",
          email: "",
          title: "",
          role_ids: [],
          account_ids: [],
          country_ids: [],
          direct_permissions: [],
          time_zone: "",
          status: Constants.STATUSES.ACTIVE,
          mailer: false,
          metis_mailer: false,
          rating_mailer: false,
          quality_mailer: false,
        },
  });

const UserForm = ({
  form,
  mode = "edit",
  edit,
  onSubmit,
  user,
  data,
  loading,
  errors
}: Props) => {
  const selectedAccounts = form.watch("account_ids") || [];
  console.log(data);
  const removeTag = (value: string) => {
    form.setValue(
      "account_ids",
      selectedAccounts.filter((item: string) => item !== value)
    );
  };

  const handleSubmit = (data: any) => {
    const payload = {
      user: {...data, role_ids: [data.role_ids]},
    };

    onSubmit(payload as any);
  };

  useEffect(() => {
    if (!edit)
      form.reset({
        ...user,
        role_ids: user?.roles.map((role) => role.id.toString()) || [],
        account_ids:
          user?.accounts.map((account) => account.id.toString()) || [],
        country_ids:
          user?.countries.map((country) => country.id.toString()) || [],
      });
  }, [edit, user]);

  useEffect(() => {
    if (user) {
      form.reset({
        ...user,
        role_ids: user.roles.map((role) => role.id.toString()) || [],
        account_ids:
          user.accounts.map((account) => account.id.toString()) || [],
        country_ids:
          user.countries.map((country) => country.id.toString()) || [],
      });
    }
  }, [user]);

  useEffect(() => {
    if (errors) {
      Object.entries(errors).forEach(([key, messages]) => {
        form.setError(key as keyof typeof form.getValues, {
          type: "manual",
          message: `${capitalize(key)} ${messages[0]}`,
        });
      });
    }
  }
  , [errors]);

  const isClientSelected = form.watch("role_ids").includes("7");

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="space-y-8 w-full"
      >
        <div className="flex gap-4">
          <FormField
            control={form.control}
            name="first_name"
            render={({ field }) => (
              <FormItem className="w-1/2">
                <FormLabel>First Name*</FormLabel>
                <FormControl>
                  <Input {...field} disabled={!edit} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="last_name"
            render={({ field }) => (
              <FormItem className="w-1/2">
                <FormLabel>Last Name*</FormLabel>
                <FormControl>
                  <Input {...field} disabled={!edit} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex gap-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem className="w-1/2">
                <FormLabel>Email*</FormLabel>
                <FormControl>
                  <Input
                    placeholder="account@nmqdigital.com"
                    danger={!!form.formState.errors[field.name]}
                    {...field}
                    type="email"
                    disabled={!edit}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="role_ids"
            render={({ field }) => (
              <FormItem className="w-1/2">
                <FormLabel>Access Level*</FormLabel>
                <FormControl>
                  <Select
                    options={
                      data.all_roles.map((role) => ({
                        label: role.name,
                        value: role.id.toString(),
                      })) || []
                    }
                    value={field.value}
                    onChange={(value) => field.onChange(value)}
                    disabled={!edit}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex gap-4">
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem className="w-1/2">
                <FormLabel>Title*</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value || undefined}
                    disabled={!edit}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="time_zone"
            render={({ field }) => (
              <FormItem className="w-1/2">
                <FormLabel>Time Zone*</FormLabel>
                <FormControl>
                  <Select
                    options={data.all_time_zones.map((time_zone) => ({
                      label: time_zone,
                      value: time_zone,
                    }))}
                    value={field.value}
                    onChange={(value) => field.onChange(value)}
                    disabled={!edit}
                    enabledSearch={true}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        { edit && (
          <div className="flex flex-wrap gap-2 mt-2">
            {(selectedAccounts || []).map((item: string) => (
              <span key={item} className="bg-gray-200 dark:bg-primary dark:text-black px-2 py-1 rounded flex items-center gap-1 text-xs">
                {data.all_countries.find((opt) => opt.id.toString() == item)?.name}
                <button onClick={() => removeTag(item)}>
                  <X size={16} />
                </button>
              </span>
            ))}
          </div>
        )}
        
        <FormField
          control={form.control}
          name="account_ids"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel>Accounts*</FormLabel>
              <FormControl>
                <Select
                  multiple
                  options={data.all_countries
                    .sort((a, b) => a.name - b.name)
                    .map((account) => ({
                      label: account.name,
                      value: account.id.toString(),
                    }))}
                  value={field.value}
                  onChange={(value) => field.onChange(value)}
                  disabled={!edit}
                  enabledSearch={true}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="py-2">
          <div className="flex flex-wrap gap-3 mt-2">
            <FormField
              control={form.control}
              name="mailer"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <CheckboxButton
                      disabled={!edit}
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    >
                      Mailer
                    </CheckboxButton>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="metis_mailer"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <CheckboxButton
                      disabled={!edit}
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    >
                      Metis Mailer
                    </CheckboxButton>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="rating_mailer"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <CheckboxButton
                      disabled={!edit}
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    >
                      Rating Mailer
                    </CheckboxButton>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="quality_mailer"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <CheckboxButton
                      disabled={!edit}
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    >
                      Quality Mailer
                    </CheckboxButton>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {isClientSelected && (
              <FormField
                control={form.control}
                name="client_admin"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <CheckboxButton
                        disabled={!edit}
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      >
                        Client Admin
                      </CheckboxButton>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </div>
        </div>
        <FormField
          control={form.control}
          name="status"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Status*</FormLabel>
              <FormControl>
                <div>
                  <Switch
                    checked={field.value === Constants.STATUSES.ACTIVE}
                    onCheckedChange={(checked) =>
                      field.onChange(
                        checked
                          ? Constants.STATUSES.ACTIVE
                          : Constants.STATUSES.INACTIVE
                      )
                    }
                    disabled={!edit}
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex gap-2">
          {mode === "create" && (
            <Link href="/admin/users">
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </Link>
          )}
          <Button type="submit" disabled={!edit || loading}>
            Save
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default UserForm;
